import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Minus, ArrowLeft, ChevronRight, Save, Flag } from "lucide-react";

/*
  Round 1 (initial) score-entry redesign — preview-only.
  Two variants the user can flip between via tabs.
  Self-contained, no API calls. Uses the same design tokens as the rest of the app.
*/

// Sample par data for an 18-hole course (used by both variants)
const SAMPLE_PARS = [4, 4, 5, 3, 4, 4, 5, 3, 4, 4, 4, 5, 3, 4, 4, 5, 3, 4]; // total 71

function PageHeader({ active, onSwitch }) {
  return (
    <header className="bg-surface border-b border-line">
      <div className="max-w-5xl mx-auto px-4 py-4 sm:px-8 sm:py-5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Link to="/dashboard" className="text-ink-muted hover:text-ink transition-colors">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div className="min-w-0">
            <div className="text-[11px] font-semibold tracking-[0.08em] uppercase text-ink-muted">Preview</div>
            <h1 className="text-base sm:text-lg font-semibold tracking-tight text-ink truncate">Round 1 entry · pick a layout</h1>
          </div>
        </div>
        <div className="ge-tabs">
          <button
            type="button"
            data-testid="preview-tab-a"
            onClick={() => onSwitch("A")}
            className={`ge-tab ${active === "A" ? "active" : ""}`}
          >
            Variant A
          </button>
          <button
            type="button"
            data-testid="preview-tab-b"
            onClick={() => onSwitch("B")}
            className={`ge-tab ${active === "B" ? "active" : ""}`}
          >
            Variant B
          </button>
        </div>
      </div>
    </header>
  );
}

function Stepper({ value, onChange, min = 0, max = 10, size = "md", testid }) {
  const sizes = {
    sm: { btn: "w-8 h-8", num: "text-xl w-10" },
    md: { btn: "w-10 h-10", num: "text-2xl w-12" },
    lg: { btn: "w-11 h-11", num: "text-3xl w-14" },
  }[size];
  return (
    <div className="flex items-center gap-2.5">
      <button
        type="button"
        onClick={() => onChange(Math.max(min, value - 1))}
        disabled={value <= min}
        className={`${sizes.btn} rounded-full border border-line bg-surface text-ink hover:bg-app hover:border-line-strong active:scale-95 transition-all flex items-center justify-center disabled:opacity-30`}
        data-testid={testid ? `${testid}-minus` : undefined}
        aria-label="Decrease"
      >
        <Minus className="w-4 h-4" />
      </button>
      <div
        className={`font-mono tabular-nums font-semibold tracking-tight text-center text-ink leading-none ${sizes.num}`}
        data-testid={testid ? `${testid}-value` : undefined}
      >
        {value || "—"}
      </div>
      <button
        type="button"
        onClick={() => onChange(Math.min(max, value + 1))}
        disabled={value >= max}
        className={`${sizes.btn} rounded-full bg-ink text-white hover:bg-ink-hover active:scale-95 transition-all flex items-center justify-center disabled:opacity-30`}
        data-testid={testid ? `${testid}-plus` : undefined}
        aria-label="Increase"
      >
        <Plus className="w-4 h-4" />
      </button>
    </div>
  );
}

/* ───────── VARIANT A ─────────
   Single-column tap list. One hole per row.
   Sticky summary bar at top: total / vs par / X of 18 entered.
   Par is shown in a clearly labelled green pill so it can never be confused with the score.
*/
function VariantA() {
  const [scores, setScores] = useState(Array(18).fill(0));
  const setHole = (i, v) => setScores((s) => s.map((x, idx) => (idx === i ? v : x)));

  const total = scores.reduce((s, v) => s + v, 0);
  const totalPar = SAMPLE_PARS.reduce((s, v) => s + v, 0);
  const entered = scores.filter((v) => v > 0).length;
  const playedPar = scores.reduce((s, v, i) => (v > 0 ? s + SAMPLE_PARS[i] : s), 0);
  const diff = total - playedPar;
  const diffStr = entered === 0 ? "—" : diff === 0 ? "E" : diff > 0 ? `+${diff}` : `${diff}`;

  return (
    <div data-testid="variant-a">
      {/* Sticky live summary */}
      <div className="sticky top-0 z-20 bg-app/90 backdrop-blur-md border-b border-line">
        <div className="max-w-3xl mx-auto px-4 sm:px-8 py-3 flex items-center justify-between gap-4">
          <div className="flex items-baseline gap-3 min-w-0">
            <div>
              <div className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-ink-muted">Round total</div>
              <div className="font-mono tabular-nums text-2xl sm:text-3xl font-semibold text-ink leading-none mt-1">{total || 0}</div>
            </div>
            <div className="hidden sm:block w-px h-8 bg-line"></div>
            <div className="hidden sm:block">
              <div className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-ink-muted">Vs par</div>
              <div className={`font-mono tabular-nums text-xl font-semibold leading-none mt-1 ${
                entered === 0 ? "text-ink-soft" : diff < 0 ? "text-accent" : diff > 0 ? "text-ink-muted" : "text-ink"
              }`}>{diffStr}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-ink-muted">Entered</div>
            <div className="font-mono tabular-nums text-base font-semibold text-ink leading-none mt-1">{entered} / 18</div>
            <div className="mt-1.5 w-24 h-1 bg-line rounded-full overflow-hidden">
              <div className="h-full bg-accent transition-all" style={{ width: `${(entered / 18) * 100}%` }}></div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-8 py-6">
        <p className="text-[13px] text-ink-muted mb-4">Tap <b className="text-ink font-semibold">+</b> / <b className="text-ink font-semibold">−</b> to enter your score for each hole.</p>

        <div className="bg-surface border border-line rounded-2xl overflow-hidden divide-y divide-line">
          {SAMPLE_PARS.map((par, i) => {
            const score = scores[i];
            const empty = score === 0;
            const diffOnHole = score - par;
            const tone = empty
              ? "text-ink-soft"
              : diffOnHole < 0
                ? "text-accent"
                : diffOnHole > 0
                  ? "text-ink-soft"
                  : "text-ink";
            const diffLabel = empty ? "" : diffOnHole === 0 ? "E" : diffOnHole > 0 ? `+${diffOnHole}` : `${diffOnHole}`;

            return (
              <div
                key={i}
                data-testid={`a-hole-${i + 1}`}
                className="grid grid-cols-[40px_64px_1fr_auto] items-center gap-3 sm:gap-4 px-4 sm:px-5 py-3.5 hover:bg-app/60 transition-colors"
              >
                {/* Hole # */}
                <div className="font-mono tabular-nums text-ink-muted text-sm font-medium">
                  {(i + 1).toString().padStart(2, "0")}
                </div>

                {/* Par chip — visually unmistakable, sits to the LEFT of the score */}
                <div className="inline-flex items-center justify-center font-mono tabular-nums text-[11px] font-semibold uppercase tracking-[0.08em] px-2 py-1 rounded-md bg-accent-tint text-accent-deep border border-accent/20">
                  Par {par}
                </div>

                {/* Score readout */}
                <div className="flex items-baseline gap-2">
                  <span className={`font-mono tabular-nums text-[28px] font-semibold leading-none ${tone}`}>
                    {empty ? "—" : score}
                  </span>
                  {!empty && (
                    <span className={`text-[12px] font-mono font-semibold ${
                      diffOnHole < 0 ? "text-accent" : diffOnHole > 0 ? "text-ink-soft" : "text-ink-muted"
                    }`}>{diffLabel}</span>
                  )}
                </div>

                {/* Stepper */}
                <Stepper value={score} onChange={(v) => setHole(i, v)} testid={`a-hole-${i + 1}`} size="sm" />
              </div>
            );
          })}
        </div>

        <div className="mt-6 flex items-center gap-3">
          <button
            type="button"
            disabled={entered < 18}
            className="flex-1 inline-flex items-center justify-center gap-2 bg-ink hover:bg-ink-hover text-white font-semibold py-3.5 rounded-xl disabled:opacity-40 transition-all"
          >
            <Save className="w-4 h-4" />
            <span>{entered === 18 ? `Submit round · ${total}` : `Submit round · ${18 - entered} hole${18 - entered === 1 ? "" : "s"} left`}</span>
          </button>
        </div>

        <div className="mt-3 text-[12px] text-ink-soft text-center">
          Variant A · Single-column tap list · Par sits in a green pill so it can't be mistaken for your score.
        </div>
      </div>
    </div>
  );
}

/* ───────── VARIANT B ─────────
   Hole-by-hole "carousel" — one hole at a time, fullscreen-feel.
   Big par chip + huge score number + stepper. Bottom strip shows mini progress for all 18.
   Designed to be the absolute fastest, lowest-friction phone flow.
*/
function VariantB() {
  const [scores, setScores] = useState(Array(18).fill(0));
  const [active, setActive] = useState(0);
  const [reviewing, setReviewing] = useState(false);
  const setHole = (i, v) => setScores((s) => s.map((x, idx) => (idx === i ? v : x)));

  const total = scores.reduce((s, v) => s + v, 0);
  const totalParAll = SAMPLE_PARS.reduce((s, v) => s + v, 0);
  const entered = scores.filter((v) => v > 0).length;
  const par = SAMPLE_PARS[active];
  const score = scores[active];
  const diff = score - par;
  const diffLabel = score === 0 ? "" : diff === 0 ? "Even" : diff < 0 ? `${Math.abs(diff)} under par` : `${diff} over par`;
  const tone =
    score === 0
      ? "text-ink-soft"
      : diff < 0
        ? "text-accent"
        : diff > 0
          ? "text-ink-soft"
          : "text-ink";

  const goNext = () => {
    if (active === 17 && entered === 18) {
      setReviewing(true);
      return;
    }
    setActive((a) => Math.min(17, a + 1));
  };
  const goPrev = () => setActive((a) => Math.max(0, a - 1));

  if (reviewing) {
    const totalDiff = total - totalParAll;
    const totalDiffStr = totalDiff === 0 ? "E" : totalDiff > 0 ? `+${totalDiff}` : `${totalDiff}`;
    return (
      <div data-testid="variant-b-review" className="max-w-3xl mx-auto px-4 sm:px-8 py-6">
        <div className="text-[11px] font-semibold tracking-[0.08em] uppercase text-ink-muted mb-1.5">Review &amp; submit</div>
        <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-ink mb-1">Looks right?</h2>
        <p className="text-[14px] text-ink-muted mb-5">Tap any hole to fix it before submitting.</p>

        <div className="ge-stat-grid mb-5">
          <div className="ge-stat">
            <div className="ge-stat-label">Round total</div>
            <div className="ge-stat-value">{total}</div>
          </div>
          <div className="ge-stat">
            <div className="ge-stat-label">Course par</div>
            <div className="ge-stat-value">{totalParAll}</div>
          </div>
          <div className="ge-stat">
            <div className="ge-stat-label">Vs par</div>
            <div className={`ge-stat-value ${totalDiff < 0 ? "text-accent" : totalDiff > 0 ? "text-ink-muted" : ""}`}>{totalDiffStr}</div>
          </div>
          <div className="ge-stat">
            <div className="ge-stat-label">Holes under</div>
            <div className="ge-stat-value text-accent">{scores.filter((s, i) => s > 0 && s < SAMPLE_PARS[i]).length}</div>
          </div>
        </div>

        <div className="bg-surface border border-line rounded-2xl overflow-hidden divide-y divide-line">
          {SAMPLE_PARS.map((p, i) => {
            const s = scores[i];
            const d = s - p;
            const t = d < 0 ? "text-accent" : d > 0 ? "text-ink-soft" : "text-ink";
            return (
              <button
                key={i}
                type="button"
                onClick={() => { setReviewing(false); setActive(i); }}
                data-testid={`b-review-hole-${i + 1}`}
                className="w-full grid grid-cols-[40px_72px_1fr_auto] items-center gap-3 px-4 sm:px-5 py-3 hover:bg-app/60 transition-colors text-left"
              >
                <div className="font-mono tabular-nums text-ink-muted text-sm font-medium">{(i + 1).toString().padStart(2, "0")}</div>
                <div className="inline-flex items-center justify-center font-mono tabular-nums text-[11px] font-semibold uppercase tracking-[0.08em] px-2 py-1 rounded-md bg-accent-tint text-accent-deep border border-accent/20">
                  Par {p}
                </div>
                <div className={`font-mono tabular-nums text-2xl font-semibold ${t}`}>
                  {s}
                  {d !== 0 && <span className="text-[12px] ml-1.5 font-medium">{d > 0 ? `+${d}` : d}</span>}
                </div>
                <div className="text-[12px] text-ink-soft">Tap to edit</div>
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-2 gap-3 mt-5">
          <button
            type="button"
            onClick={() => { setReviewing(false); setActive(17); }}
            className="ge-btn-outline justify-center"
            data-testid="b-review-back"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </button>
          <button
            type="button"
            data-testid="b-review-submit"
            className="inline-flex items-center justify-center gap-2 bg-ink hover:bg-ink-hover text-white font-semibold py-3 rounded-xl transition-all"
          >
            <Save className="w-4 h-4" />
            <span>Submit round · {total}</span>
          </button>
        </div>
        <div className="mt-3 text-[12px] text-ink-soft text-center">
          Variant B · Review &amp; submit step · Final scorecard before locking in.
        </div>
      </div>
    );
  }

  return (
    <div data-testid="variant-b">
      <div className="max-w-3xl mx-auto px-4 sm:px-8 py-6">
        {/* Top stat band */}
        <div className="ge-stat-grid mb-6">
          <div className="ge-stat">
            <div className="ge-stat-label">Round total</div>
            <div className="ge-stat-value">{total || 0}</div>
          </div>
          <div className="ge-stat">
            <div className="ge-stat-label">Entered</div>
            <div className="ge-stat-value">{entered}<span className="text-ink-soft text-base font-mono ml-1">/18</span></div>
          </div>
          <div className="ge-stat">
            <div className="ge-stat-label">Through</div>
            <div className="ge-stat-value">{active + 1}<span className="text-ink-soft text-base font-mono ml-1">/18</span></div>
          </div>
          <div className="ge-stat">
            <div className="ge-stat-label">Course par</div>
            <div className="ge-stat-value">{SAMPLE_PARS.reduce((s, v) => s + v, 0)}</div>
          </div>
        </div>

        {/* Big single-hole card */}
        <div className="bg-surface border border-line rounded-2xl p-6 sm:p-8 relative">
          <div className="flex items-start justify-between mb-6">
            <div>
              <div className="text-[11px] font-semibold tracking-[0.08em] uppercase text-ink-muted">Hole</div>
              <div className="font-mono tabular-nums text-5xl sm:text-6xl font-semibold tracking-tight text-ink leading-none mt-1.5">
                {(active + 1).toString().padStart(2, "0")}
              </div>
            </div>
            <div className="inline-flex items-center gap-1.5 font-mono tabular-nums text-[12px] font-semibold uppercase tracking-[0.08em] px-2.5 py-1.5 rounded-lg bg-accent-tint text-accent-deep border border-accent/20">
              <Flag className="w-3 h-3" />
              <span>Par {par}</span>
            </div>
          </div>

          {/* Score */}
          <div className="text-center my-6 sm:my-8">
            <div className="text-[11px] font-semibold tracking-[0.08em] uppercase text-ink-muted mb-2">Your score</div>
            <div className={`font-mono tabular-nums text-[88px] sm:text-[120px] font-semibold tracking-tight leading-none ${tone}`}>
              {score === 0 ? "—" : score}
            </div>
            <div className={`mt-2 text-sm font-medium ${
              score === 0 ? "text-ink-soft" : diff < 0 ? "text-accent" : diff > 0 ? "text-ink-muted" : "text-ink"
            }`}>
              {diffLabel || "Tap + to enter your score"}
            </div>
          </div>

          {/* Stepper */}
          <div className="flex items-center justify-center gap-6 mb-6">
            <button
              type="button"
              onClick={() => setHole(active, Math.max(0, score - 1))}
              disabled={score <= 0}
              className="w-14 h-14 rounded-full border border-line bg-surface text-ink hover:bg-app hover:border-line-strong active:scale-95 transition-all flex items-center justify-center disabled:opacity-30"
              data-testid={`b-hole-${active + 1}-minus`}
            >
              <Minus className="w-6 h-6" />
            </button>
            <button
              type="button"
              onClick={() => setHole(active, Math.min(10, score + 1))}
              disabled={score >= 10}
              className="w-14 h-14 rounded-full bg-ink text-white hover:bg-ink-hover active:scale-95 transition-all flex items-center justify-center disabled:opacity-30"
              data-testid={`b-hole-${active + 1}-plus`}
            >
              <Plus className="w-6 h-6" />
            </button>
          </div>

          {/* Nav */}
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={goPrev}
              disabled={active === 0}
              className="ge-btn-outline justify-center disabled:opacity-30"
              data-testid="b-prev"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Previous</span>
            </button>
            <button
              type="button"
              onClick={goNext}
              disabled={(active === 17 && entered < 18) || score === 0}
              className="ge-btn-primary justify-center disabled:opacity-30"
              data-testid="b-next"
            >
              <span>{active === 17 ? (entered === 18 ? "Review & submit" : "Done") : "Next hole"}</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Mini scorecard strip — quick jump + at-a-glance */}
        <div className="mt-6 bg-surface border border-line rounded-xl p-3">
          <div className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-ink-muted mb-2 px-1">All holes</div>
          <div className="grid grid-cols-9 gap-1.5">
            {SAMPLE_PARS.map((p, i) => {
              const s = scores[i];
              const isActive = i === active;
              const empty = s === 0;
              const d = s - p;
              const tone =
                empty
                  ? "text-ink-soft bg-app"
                  : d < 0
                    ? "text-accent bg-accent-tint"
                    : d > 0
                      ? "text-ink-soft bg-app"
                      : "text-ink bg-surface";
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => setActive(i)}
                  data-testid={`b-jump-${i + 1}`}
                  className={`relative rounded-md py-2 text-center transition-all border ${
                    isActive ? "border-ink ring-2 ring-ink/20" : "border-line hover:border-line-strong"
                  } ${tone}`}
                >
                  <div className="font-mono tabular-nums text-[9.5px] text-ink-muted leading-none">{i + 1}</div>
                  <div className="font-mono tabular-nums text-[15px] font-semibold leading-none mt-1">{empty ? "—" : s}</div>
                </button>
              );
            })}
          </div>
        </div>

        <button
          type="button"
          disabled={entered < 18}
          onClick={() => setReviewing(true)}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-ink hover:bg-ink-hover text-white font-semibold py-3.5 rounded-xl disabled:opacity-40 transition-all"
          data-testid="b-review-cta"
        >
          <Save className="w-4 h-4" />
          <span>{entered === 18 ? `Review & submit · ${total}` : `${18 - entered} hole${18 - entered === 1 ? "" : "s"} left`}</span>
        </button>

        <div className="mt-3 text-[12px] text-ink-soft text-center">
          Variant B · Hole-by-hole flow · One hole at a time, big readouts, mini-card to jump around.
        </div>
      </div>
    </div>
  );
}

export default function Round1MockupPreview() {
  const [variant, setVariant] = useState("A");
  return (
    <div className="min-h-screen bg-app">
      <PageHeader active={variant} onSwitch={setVariant} />
      {variant === "A" ? <VariantA /> : <VariantB />}
    </div>
  );
}
